import React, { useMemo, useState } from 'react';

import { Divider, Group, Paper, Stack, Text, Title, createStyles, px, useMantineTheme } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import CardInfoLine from 'components/InfoCard/CardInfoLine';
import InvoicesTable, { InvoicesTableSearchFilters } from 'components/invoices/InvoicesTable';
import { useLocalization } from 'context/localization-context';
import { AthletePackageRetrieve } from 'types';

// The tab is already scoped to one package, so search, product type and billing
// source cannot narrow anything here — the billing source column is even hidden.

const useStyles = createStyles(theme => ({
  infoContainer: {
    display: 'grid',
    gridTemplateColumns: 'auto min-content auto min-content auto',
    columnGap: theme.spacing.xl,
    alignItems: 'start',
    [theme.fn.smallerThan('sm')]: {
      gridTemplateColumns: '3fr 1fr 2fr',
      gridTemplateRows: 'auto',
      gridTemplateAreas: `
        'firstLabelGrid . secondLabelGrid'
        'line line line'
        'thirdLabelGrid thirdLabelGrid thirdLabelGrid'
      `,
    },
  },
  firstLabelGrid: {
    display: 'grid',
    rowGap: px(theme.spacing.xs) / 2,
    columnGap: theme.spacing.sm,
    alignItems: 'center',
    gridTemplateColumns: 'min-content auto',
    gridTemplateRows: 'auto',
    whiteSpace: 'nowrap',
    [theme.fn.smallerThan('sm')]: {
      gridArea: 'firstLabelGrid',
      paddingLeft: theme.spacing.xs,
    },
  },
  secondLabelGrid: {
    display: 'grid',
    rowGap: px(theme.spacing.xs) / 2,
    columnGap: theme.spacing.sm,
    alignItems: 'center',
    gridTemplateColumns: 'min-content auto',
    gridTemplateRows: 'auto',
    whiteSpace: 'nowrap',
    [theme.fn.smallerThan('sm')]: {
      gridArea: 'secondLabelGrid',
      paddingRight: theme.spacing.xs,
    },
  },
  thirdLabelGrid: {
    display: 'grid',
    rowGap: px(theme.spacing.xs) / 2,
    columnGap: theme.spacing.sm,
    alignItems: 'center',
    gridTemplateColumns: 'min-content auto',
    gridTemplateRows: 'auto',
    whiteSpace: 'nowrap',
    [theme.fn.smallerThan('sm')]: {
      gridArea: 'thirdLabelGrid',
      paddingLeft: theme.spacing.xs,
    },
  },
  line: {
    [theme.fn.smallerThan('sm')]: {
      width: '100%',
      gridArea: 'line',
    },
  },
}));

interface IInvoicesTabProps {
  athletePackage: AthletePackageRetrieve;
  permission?: boolean;
}

const InvoicesTab = ({ athletePackage, permission = true }: IInvoicesTabProps): JSX.Element => {
  const { classes } = useStyles();
  const theme = useMantineTheme();
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.sm}`, false);

  const { formatCurrency, copy, isTaxInclusive } = useLocalization();

  // A package holds a handful of invoices, so the tab lists them unfiltered.
  const searchFilters: InvoicesTableSearchFilters = {
    athlete: athletePackage.athlete,
    athletePackage: athletePackage.athletePackageId,
  };

  const [firstInvoicePrice, setFirstInvoicePrice] = useState<number>(0);
  const totalPrice = useMemo(() => {
    if (isTaxInclusive) {
      return athletePackage.billingPlan.totalPrice / 100;
    } else {
      // Total Price Pre Tax (US/CA)
      return (
        (athletePackage.billingPlan.totalPrice -
          athletePackage.billingPlan.installments[0].price +
          firstInvoicePrice) /
        100
      );
    }
  }, [
    athletePackage.billingPlan.installments,
    athletePackage.billingPlan.totalPrice,
    firstInvoicePrice,
    isTaxInclusive,
  ]);

  return (
    <>
      <Stack>
        <Group position='apart'>
          <Title order={3}>Package Billing Plan</Title>
        </Group>

        <Paper
          shadow='sm'
          radius='md'
          py='sm'
          px={isDesktop ? 'xl' : undefined}
          withBorder
          sx={{ alignSelf: isDesktop ? 'start' : 'center' }}
        >
          <div className={classes.infoContainer}>
            <div className={classes.firstLabelGrid}>
              <CardInfoLine label='Billing Plan' description={athletePackage.billingPlan.name} />
              <CardInfoLine
                label={
                  copy.components.AthletePackageDetail.InvoicesTab
                    .packageBillingPlanTotalPricePreTaxFirstColumn
                }
                description={formatCurrency(totalPrice)}
              />
            </div>

            <Divider orientation='vertical' my='-sm' />

            <div className={classes.secondLabelGrid}>
              <CardInfoLine
                label='# of Installments'
                description={`${athletePackage.billingPlan.numberOfInstallments}`}
              />
              <CardInfoLine
                label='Frequency'
                description={
                  athletePackage.billingPlan.numberOfInstallments > 1
                    ? `${athletePackage.billingPlan.installmentIntervalCount} ${
                        athletePackage.billingPlan.installmentInterval
                      }${(athletePackage.billingPlan?.installmentIntervalCount || 0) > 1 ? 's' : ''}`
                    : 'Once'
                }
              />
              <CardInfoLine label='Invoice AutoPay' description={athletePackage.autoPay ? 'On' : 'Off'} />
            </div>

            {isDesktop ? (
              <Divider orientation='vertical' my='-sm' />
            ) : (
              <Divider my='sm' className={classes.line} />
            )}

            <div className={classes.thirdLabelGrid}>
              <Text fs='italic' c='dimmed' fz='xs' ta='right' my={2} sx={{ alignSelf: 'start' }}>
                {copy.components.AthletePackageDetail.InvoicesTab.packageBillingPlanInstallmentsThirdColumn}
              </Text>
              <Group spacing={0}>
                {athletePackage.billingPlan.installments.map(installment => {
                  if (athletePackage.isProrated && installment.installmentNumber === 1) {
                    return (
                      <Text fz='sm' fs='italic' pr='sm' key={installment.installmentId}>
                        {`Installment ${installment.installmentNumber} - ${formatCurrency(
                          (firstInvoicePrice || 0) / 100,
                        )}`}
                      </Text>
                    );
                  } else {
                    return (
                      <Text fz='sm' fs='italic' pr='sm' key={installment.installmentId}>
                        {`Installment ${installment.installmentNumber} - ${formatCurrency(
                          (installment.price || 0) / 100,
                        )}`}
                      </Text>
                    );
                  }
                })}
              </Group>
            </div>
          </div>
        </Paper>
      </Stack>

      <InvoicesTable
        searchFilters={searchFilters}
        enableHook={!!athletePackage}
        removeColumns={['athlete', 'billingSource']}
        permission={permission}
        setFirstInvoicePrice={setFirstInvoicePrice}
      />
    </>
  );
};

export default InvoicesTab;
